/**
 * urlaSectionVisibility.js — smart-subset URLA section resolver.
 *
 * The full URLA (defined in src/config/mismoFormFields.js) has 17 sections. Showing
 * every section to every borrower is the right intent (it is what loan officers
 * expect a "real" application to look like) but the wrong UX. We collapse the form
 * to only the sections that actually apply, based on what we already know from the
 * recommendation phase.
 *
 * Resolver inputs:
 *   - productRecommendation: the answers collected during the assessment phase
 *   - applicationData: in-flight URLA answers (so liveness drives subsequent gating)
 *
 * Returns the ordered list of section keys to render.
 */
import { MISMO_SECTIONS } from '../config/mismoFormFields';

// Canonical section order (mirrors getMismoSections() in mismoFormFields.js).
const SECTION_ORDER = [
  'borrowerInfo',
  'contactInfo',
  'currentAddress',
  'previousAddress',
  'propertyInfo',
  'loanInfo',
  'employmentIncome',
  'otherIncome',
  'assets',
  'credits',
  'giftsGrants',
  'liabilities',
  'realEstateOwned',
  'declarations',
  'militaryService',
  'demographics',
  'acknowledgment',
];

const truthy = (v) => v === true || v === 'true' || v === 'yes' || v === 'Yes';

const hasAnyEntries = (section) =>
  Array.isArray(section?.entries) && section.entries.length > 0;

const monthsAtAddress = (currentAddress) => {
  const y = parseInt(currentAddress?.yearsAtAddress, 10) || 0;
  const m = parseInt(currentAddress?.monthsAtAddress, 10) || 0;
  return y * 12 + m;
};

/**
 * Decide which URLA sections to render for this borrower.
 *
 * Skip rules (MVP defaults):
 *  - previousAddress    → only when current address < 24 months
 *  - giftsGrants        → only on purchase OR if user already added one
 *  - credits            → only on purchase
 *  - realEstateOwned    → only when borrower indicates they own other property
 *  - militaryService    → only when assessment flagged military OR borrower toggles VA interest
 *  - demographics       → always shown (HMDA), but rendered last and clearly optional
 *  - acknowledgment     → always shown last
 */
export function getVisibleUrlaSectionKeys(productRecommendation = {}, applicationData = {}) {
  const purpose = productRecommendation.purchaseOrRefinance; // 'purchase' | 'refinance'
  const isPurchase = purpose === 'purchase';

  const militaryFromAssessment = truthy(productRecommendation.militaryService);
  const militaryFromForm = truthy(applicationData.militaryService?.everServed);

  const monthsAtCurrent = monthsAtAddress(applicationData.currentAddress);
  const showPreviousAddress = monthsAtCurrent > 0 && monthsAtCurrent < 24;

  const userAddedGifts = hasAnyEntries(applicationData.giftsGrants);
  const userAddedCredits = hasAnyEntries(applicationData.credits);
  const userAddedREO = hasAnyEntries(applicationData.realEstateOwned);

  // Conditional inclusions
  const conditional = {
    previousAddress: showPreviousAddress,
    credits: isPurchase || userAddedCredits,
    giftsGrants: isPurchase || userAddedGifts,
    realEstateOwned: userAddedREO,                            // opt-in
    militaryService: militaryFromAssessment || militaryFromForm,
  };

  return SECTION_ORDER.filter((key) => {
    if (key in conditional) return conditional[key];
    return true;
  });
}

/**
 * Hydrate the section keys into the actual MISMO_SECTIONS objects.
 */
export function getVisibleUrlaSections(productRecommendation, applicationData) {
  const keys = getVisibleUrlaSectionKeys(productRecommendation, applicationData);
  return keys.map((k) => MISMO_SECTIONS[k]).filter(Boolean);
}

/**
 * Helper: which of the section's fields should render given the section's own data.
 *
 * mismoFormFields.js fields can carry:
 *   - showFor:            ['valueA', 'valueB']  → only render when a *driver* field equals one of these
 *   - showWhen:           { field: 'x', value: 'y' } → only render when sibling field equals value
 *   - showForLoanPurpose: ['Purchase', 'Refinance']  → uses loanInfo.loanPurpose
 *
 * Returns true if the field should be shown.
 */
export function isFieldVisible(field, sectionData = {}, allApplicationData = {}) {
  // showForLoanPurpose: reads from loanInfo.loanPurpose specifically
  if (Array.isArray(field.showForLoanPurpose)) {
    const lp = allApplicationData?.loanInfo?.loanPurpose;
    if (!lp || !field.showForLoanPurpose.includes(lp)) return false;
  }

  // showWhen: sibling-field equality
  if (field.showWhen?.field) {
    if (sectionData?.[field.showWhen.field] !== field.showWhen.value) return false;
  }

  // showFor: parent driver field. The driver field is convention-based — we look
  // for the most common driver fields in this section's data and check if any
  // matches the showFor whitelist.
  if (Array.isArray(field.showFor) && field.showFor.length > 0) {
    const driverCandidates = [
      sectionData?.employmentType,
      sectionData?.assetType,
      sectionData?.liabilityType,
      sectionData?.incomeSourceType,
      sectionData?.creditType,
      sectionData?.giftSource,
      sectionData?.propertyType,
      sectionData?.loanPurpose,
      sectionData?.donorRelationship,
    ];
    if (!driverCandidates.some((v) => v && field.showFor.includes(v))) return false;
  }

  return true;
}

export default {
  getVisibleUrlaSectionKeys,
  getVisibleUrlaSections,
  isFieldVisible,
};
